import { Check, Copy, Share2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { authFetch } from '../auth'

export type CalculatorPlatform = 'jd' | 'pinduoduo'
export type CalculatorShareAccess = 'read' | 'edit'

interface ShareCandidate {
  userId: string
  username: string
  canEdit: boolean
  accessLevel: CalculatorShareAccess | null
}

interface SharingPayload {
  recordId: string
  revision: number
  candidates: ShareCandidate[]
  shares: Array<{ userId: string; username: string; accessLevel: CalculatorShareAccess }>
}

interface CalculatorShareDialogProps {
  platform: CalculatorPlatform
  record: { id: string; code?: string; note?: string; productName: string }
  onClose: () => void
  onSaved: () => void
}

export function CalculatorShareDialog({ platform, record, onClose, onSaved }: CalculatorShareDialogProps) {
  const [payload, setPayload] = useState<SharingPayload | null>(null)
  const [selection, setSelection] = useState<Record<string, CalculatorShareAccess>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const endpoint = `/api/calculators/${platform}/records/sharing`

  useEffect(() => {
    let active = true
    fetch(`${endpoint}?recordId=${encodeURIComponent(record.id)}`, { cache: 'no-store' })
      .then(async (response) => {
        const next = await response.json() as SharingPayload & { error?: string }
        if (!response.ok) throw new Error(next.error || '分享设置加载失败')
        return next
      })
      .then((next) => {
        if (!active) return
        setPayload(next)
        setSelection(Object.fromEntries(next.shares.map((item) => [item.userId, item.accessLevel])))
      })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : String(reason)) })
    return () => { active = false }
  }, [endpoint, record.id])

  const selectedCount = useMemo(() => Object.keys(selection).length, [selection])
  const setChecked = (candidate: ShareCandidate, checked: boolean) => {
    setSelection((current) => {
      const next = { ...current }
      if (checked) next[candidate.userId] = 'read'
      else delete next[candidate.userId]
      return next
    })
  }

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recordId: record.id,
          shares: Object.entries(selection).map(([userId, accessLevel]) => ({ userId, accessLevel })),
        }),
      })
      const next = await response.json() as SharingPayload & { error?: string }
      if (!response.ok) throw new Error(next.error || '分享设置保存失败')
      onSaved()
      onClose()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setBusy(false)
    }
  }

  const copyLink = async () => {
    const link = `${window.location.origin}/shared-calculations/${platform}/${encodeURIComponent(record.id)}`
    await navigator.clipboard?.writeText(link)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="calculator-share-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <section className="calculator-share-dialog" role="dialog" aria-modal="true" aria-labelledby="calculator-share-title">
        <header>
          <div className="calculator-share-icon"><Share2 size={18} /></div>
          <div>
            <h2 id="calculator-share-title">分享测算 {record.code || ''}</h2>
            <p>{record.note || record.productName}</p>
          </div>
          <button type="button" className="calculator-share-close" aria-label="关闭" onClick={onClose}><X size={18} /></button>
        </header>
        <div className="calculator-share-body">
          <div className="calculator-share-private">默认私有。只有保存到下方名单里的账号可以看到这条测算。</div>
          {error && <div className="form-error">{error}</div>}
          {!payload && !error && <div className="calculator-share-loading">正在读取同店账号…</div>}
          {payload && payload.candidates.length === 0 && <div className="calculator-share-empty">当前没有其他具备本店查看权限的启用账号。</div>}
          {payload && payload.candidates.length > 0 && (
            <div className="calculator-share-list">
              {payload.candidates.map((candidate) => {
                const access = selection[candidate.userId]
                return (
                  <div className="calculator-share-person" key={candidate.userId}>
                    <label>
                      <input type="checkbox" checked={Boolean(access)} onChange={(event) => setChecked(candidate, event.target.checked)} />
                      <span><b>{candidate.username}</b><small>{candidate.canEdit ? '具有计算器查看和保存权限' : '仅具有本店查看权限'}</small></span>
                    </label>
                    <select
                      aria-label={`${candidate.username}的分享权限`}
                      value={access || 'read'}
                      disabled={!access}
                      onChange={(event) => setSelection((current) => ({ ...current, [candidate.userId]: event.target.value as CalculatorShareAccess }))}
                    >
                      <option value="read">只读</option>
                      <option value="edit" disabled={!candidate.canEdit}>可编辑</option>
                    </select>
                  </div>
                )
              })}
            </div>
          )}
          <p className="calculator-share-rule">可编辑同事能保存同一记录，但不能删除、转分享、调整接收人或转移所有者。账号停用或店铺权限被移除后，访问立即失效。</p>
        </div>
        <footer>
          <button type="button" className="jdc-btn-ghost" onClick={() => void copyLink()}><Copy size={14} />{copied ? '已复制' : '复制安全链接'}</button>
          <span>{selectedCount > 0 ? `将分享给 ${selectedCount} 人` : '保存后保持仅自己可见'}</span>
          <button type="button" className="primary-button" disabled={busy || !payload} onClick={() => void save()}><Check size={14} />{busy ? '保存中…' : '保存分享设置'}</button>
        </footer>
      </section>
    </div>
  )
}
